import { fetchWallPrivacy, ACCESS } from "@padlet/wall-privacy"
import { __ } from "bits/intl"

export default {
  namespaced: true,
  state: {
    // admin wall privacy object for modification
    isPrivacyFetched: false,
    isPasswordSet: false,
    _wallPrivacyObject: null,
    wallPrivacy: null,
    errorMessage: null,
    isSaving: false,
    isSearchingContributors: false,
    contributorsSearchResults: []
  },
  getters: {
    isDirty: state => !!state.wallPrivacy && state.wallPrivacy.hasChanges,
    isContributorInvited: state => contributor => (
      state._wallPrivacyObject.isContributorInvited(contributor)
    ),
    // TODO: Why is wallPrivacy marking password walls as "secret"?
    access: state => {
      const rawAccess = state.wallPrivacy && state.wallPrivacy.access
      if (rawAccess === ACCESS.SECRET && state.isPasswordSet) {
        return ACCESS.PASSWORD_PROTECTED
      }
      return rawAccess
    },
  },
  mutations: {
    FETCHED_WALL_PRIVACY_SUCCESS(state, wallPrivacy) {
      state._wallPrivacyObject = wallPrivacy
      state.wallPrivacy = state._wallPrivacyObject.attributes
      state.isPrivacyFetched = true
      state.errorMessage = null
      state.isPasswordSet = !!wallPrivacy.password
    },
    DISCARD_SHARE_PANEL_CHANGES (state) {
      if (!state._wallPrivacyObject || !state._wallPrivacyObject.hasChanges) return
      state._wallPrivacyObject.discardChanges()
      state.wallPrivacy = state._wallPrivacyObject.attributes
      state.isPasswordSet = !!state.wallPrivacy.password
      state.isPrivacyFetched = true
      state.isSearchingContributors = false
      state.contributorsSearchResults = []
    },
    RESET_WALL_PRIVACY (state) {
      state.isPrivacyFetched = false
      state.isPasswordSet = false
      state._wallPrivacyObject = null
      state.wallPrivacy = null
      state.errorMessage = null
      state.isSaving = false
      state.isSearchingContributors = false
      state.contributorsSearchResults = []
    },
    FETCHED_WALL_PRIVACY_ERROR (state) {
      state.isPrivacyFetched = true
    },
    SET_WALL_PRIVACY_ACCESS(state, access) {
      state.isPasswordSet = false
      switch (access) {
        case ACCESS.PUBLIC:
          state._wallPrivacyObject.isPublic = true
          break
        case ACCESS.SECRET:
          state._wallPrivacyObject.isSecret = true
          break
        case ACCESS.PRIVATE:
          state._wallPrivacyObject.isPrivate = true
          break
        case ACCESS.ORGWIDE:
          state._wallPrivacyObject.isSecret = true
          state._wallPrivacyObject.isOrgwide = true
          break
        case ACCESS.PASSWORD_PROTECTED:
          state._wallPrivacyObject.isSecret = true
          state.isPasswordSet = true
          if (state._wallPrivacyObject.originalAttributes.password) {
            state._wallPrivacyObject.password = state._wallPrivacyObject.originalAttributes.password
          } else {
            state._wallPrivacyObject.password = ''
          }
          break
        default:
          break
      }
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_WALL_PRIVACY_PASSWORD(state, password) {
      state.isPasswordSet = true
      state._wallPrivacyObject.password = password
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_WALL_PRIVACY_PERMISSION(state, permission) {
      state._wallPrivacyObject.permission = permission
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    ADD_CONTRIBUTOR_TO_WALL_PRIVACY(state, {emailOrUserIdOrContributor, permission, extra}) {
      try {
        state._wallPrivacyObject.addContributor(emailOrUserIdOrContributor, permission, {...extra, synced: false})
      } catch (error) {
        console.warn(error)
      }
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY(state, emailOrUserIdOrContributor) {
      state._wallPrivacyObject.removeContributor(emailOrUserIdOrContributor)
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SET_PERMISSION_FOR_WALL_PRIVACY_CONTRIBUTOR(state, {permission, contributor}) {
      state._wallPrivacyObject.setContributorPermission(contributor, permission)
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SAVE_WALL_PRIVACY_SETTINGS(state) {
      state.isSaving = true
    },
    SAVE_WALL_PRIVACY_SETTINGS_SUCCESS(state, wallPrivacy) {
      if (!state.isPrivacyFetched) return
      state.isSaving = false
      state._wallPrivacyObject = wallPrivacy
      state.wallPrivacy = state._wallPrivacyObject.attributes
    },
    SAVE_WALL_PRIVACY_SETTINGS_FAILURE (state) {
      state.isSaving = false
    },
    SET_ERROR_MESSAGE(state, errorMessage) {
      state.errorMessage = errorMessage
    },
    CLEAR_ERROR_MESSAGE(state) {
      state.errorMessage = null
    },
    SEARCH_CONTRIBUTORS(state) {
      state.isSearchingContributors = true
    },
    SEARCH_CONTRIBUTORS_SUCCESS(state, searchResults) {
      if (!state.isPrivacyFetched) return
      state.contributorsSearchResults = searchResults.filter((r => !(r.userId && !r.username)))
      state.isSearchingContributors = false
    },
    SEARCH_CONTRIBUTORS_ERROR(state) {
      if (!state.isPrivacyFetched) return
      state.contributorsSearchResults = []
      state.isSearchingContributors = false
    },
    STOP_CONTRIBUTOR_SEARCH(state) {
      state.isSearchingContributors = false
      state.contributorsSearchResults = []
    },
    RESEND_INVITATION (state, contributor) {
      state._wallPrivacyObject.resendInvitation(contributor)
    },
  },
  actions: {
    async fetchWallPrivacy({
      rootState,
      commit
    }) {
      try {
        let wallPrivacy = await fetchWallPrivacy({
          privacyPolicyId: rootState.wall.privacy_policy.id,
          host: window.location.hostname,
          protocol: window.location.protocol.replace(':', ''),
          csrfToken: rootState.csrfToken,
        })
        commit("FETCHED_WALL_PRIVACY_SUCCESS", wallPrivacy)
      } catch (error) {
        commit('FETCHED_WALL_PRIVACY_ERROR')
        commit("SET_ERROR_MESSAGE", __('Sorry, we were unable to get your privacy settings.') + ' ' + error.message)
      }
    },
    setAccess({commit}, {access}) {
      commit("SET_WALL_PRIVACY_ACCESS", access)
    },
    setPassword({commit}, {password}) {
      commit("SET_WALL_PRIVACY_PASSWORD", password)
    },
    setWallPermission ({ commit }, { permission }) {
      commit('SET_WALL_PRIVACY_PERMISSION', permission)
    },
    async saveSettings({state, commit, dispatch}) {
      if (!state.wallPrivacy.password && state.isPasswordSet) {
        dispatch('setErrorMessage', {errorMessage: __('You have to set a password!')})
        return
      }
      commit("SAVE_WALL_PRIVACY_SETTINGS")
      try {
        let wallPrivacy = await state._wallPrivacyObject.commit()
        commit('SAVE_WALL_PRIVACY_SETTINGS_SUCCESS', wallPrivacy)
      } catch (error) {
        const message = __('Sorry! Something went wrong. Please try saving again.')
        const errorText = error.message || error.error || ''
        const errorMessage = `${message} ${errorText}`
        commit('SAVE_WALL_PRIVACY_SETTINGS_FAILURE')
        commit('SET_ERROR_MESSAGE', errorMessage)
      }
    },
    setErrorMessage({state, commit}, {errorMessage}) {
      commit("SET_ERROR_MESSAGE", errorMessage)
      state.errorMessageTimeout && clearTimeout(state.errorMessageTimeout)
      state.errorMessageTimeout = setTimeout(() => {
        commit("CLEAR_ERROR_MESSAGE")
      }, 5000)
    },
    clearErrorMessage({commit}) {
      commit("CLEAR_ERROR_MESSAGE")
    },
    addContributor({state, commit}, {emailOrUserIdOrContributor, permission, ...extra}) {
      commit("ADD_CONTRIBUTOR_TO_WALL_PRIVACY", {emailOrUserIdOrContributor, permission, extra})
    },
    removeContributor({state, commit}, emailOrUserIdOrContributor) {
      commit("REMOVE_CONTRIBUTOR_FROM_WALL_PRIVACY", emailOrUserIdOrContributor)
    },
    setPermissionForContributor({state, commit}, {permission, contributor}) {
      commit("SET_PERMISSION_FOR_WALL_PRIVACY_CONTRIBUTOR", {permission, contributor})
    },
    async searchContributors({state, dispatch, commit}, {query}) {
      commit("SEARCH_CONTRIBUTORS")
      try {
        let searchResults = await state._wallPrivacyObject.searchContributors(query)
        commit("SEARCH_CONTRIBUTORS_SUCCESS", searchResults)
      } catch (error) {
        dispatch('setErrorMessage', {errorMessage: error.message})
        commit("SEARCH_CONTRIBUTORS_ERROR", error)

      }
    },
    stopContributorSearch({commit}) {
      commit("STOP_CONTRIBUTOR_SEARCH")
    },
    resetWallPrivacy ({ commit }) {
      commit('RESET_WALL_PRIVACY')
    },
    discardWallPrivacyChanges ({ commit }) {
      commit('DISCARD_SHARE_PANEL_CHANGES')
    },
    resendInvitation ({ commit }, contributor) {
      commit('RESEND_INVITATION', contributor)
    },
  }
}